/**
 * ──────────────────────────────────────────────────────────────────────────
 *  PROFILE — single source of truth for the whole page.
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  Change any text, link, skill or timeline entry here and the UI updates.
 *  Nothing else needs to be touched for content edits.
 *
 *  HOW TO…
 *   • Change a sentence ............ edit the matching string below.
 *   • Add a skill / interest ....... push a string into the array.
 *   • Add a timeline entry ......... push an object into `items`.
 *   • Reorder sections ............. reorder the `sections` array (the big
 *                                    background number renumbers itself).
 *   • Add a brand-new section type . see src/components/timercard/SectionView.tsx
 *
 *  Localized text is `{ pt, en }`. A plain string means "same in both".
 */

import type { Localized } from "@/lib/i18n";
// Bundler resolves this to a served URL (dev) / hashed asset (build).
import iconUrl from "../../assets/icon.png";

/* ── Actions ──────────────────────────────────────────────────────────────
 * Reusable behaviour for links / buttons. Add a new `kind` here + handle it
 * in the component that consumes it to teach the page a new trick.            */
export type Action =
  | { kind: "link"; href: string }
  | { kind: "copy"; value: string };

/* ── Section shapes ───────────────────────────────────────────────────────
 * A discriminated union. Each `type` maps to one component in
 * src/components/timercard/SectionView.tsx.                                   */
interface SectionBase {
  /** Stable id, used for nav + animation keys. */
  id: string;
  /** Short nav label. */
  nav: Localized;
  /** Eyebrow above the title. */
  kicker: Localized;
}

export interface AboutSection extends SectionBase {
  type: "about";
  title: Localized;
  body: Localized;
  quote: Localized;
  interests: Localized[];
}

export interface TimelineSection extends SectionBase {
  type: "timeline";
  title: Localized;
  items: { year: Localized; title: Localized; desc: Localized }[];
}

export interface SkillsSection extends SectionBase {
  type: "skills";
  title: Localized;
  skills: Localized[];
}

export interface LinksSection extends SectionBase {
  type: "links";
  title: Localized;
  links: {
    label: Localized; // small eyebrow, e.g. "Twitter / X"
    value: string; // big handle, e.g. "@KingTimer12"
    note: Localized; // helper line
    action: Action;
  }[];
}

export interface ContactSection extends SectionBase {
  type: "contact";
  title: Localized;
  body: Localized;
  cta: { label: Localized; doneLabel: Localized; action: Action };
}

export type Section =
  | AboutSection
  | TimelineSection
  | SkillsSection
  | LinksSection
  | ContactSection;

/* ── The profile ─────────────────────────────────────────────────────────*/
export interface Profile {
  brand: { name: string; accent: string; initials: string };
  identity: {
    /** Optional avatar URL/import. `null` → initials are shown instead. */
    avatar: string | null;
    role: Localized;
    tagline: Localized;
    clockLabel: Localized;
  };
  clock: { timezone: string };
  footer: { domain: string; signature: Localized };
  sections: Section[];
}

export const profile: Profile = {
  brand: { name: "KingTimer", accent: "12", initials: "KT" },

  identity: {
    avatar: iconUrl,
    role: { pt: "Desenvolvedor · Escritor", en: "Developer · Writer" },
    tagline: {
      pt: "Aprendendo algo novo a cada volta do ponteiro.",
      en: "Learning something new with every turn of the hand.",
    },
    clockLabel: { pt: "hora de Fortaleza", en: "Fortaleza time" },
  },

  clock: { timezone: "America/Fortaleza" },

  footer: {
    domain: "kingtimer12.dev",
    signature: { pt: "feito com tempo", en: "made with time" },
  },

  sections: [
    {
      id: "sobre",
      type: "about",
      nav: { pt: "Sobre", en: "About" },
      kicker: { pt: "Sobre", en: "About" },
      title: { pt: "Quem é o KingTimer", en: "Who is KingTimer" },
      body: {
        pt: "Sou um cara que gosta de aprender coisas novas, conhecer pessoas e ajudá-las no que posso. Curto anime e mangá, adoro as comics da Merry e sou fã de Steins;Gate e Houseki no Kuni. Acima de tudo, amo meus amigos e tenho orgulho de tê-los sempre por perto.",
        en: "I'm someone who loves learning new things, meeting people and helping however I can. I'm into anime and manga, I love Merry's comics and I'm a fan of Steins;Gate and Houseki no Kuni. Above all, I love my friends and I'm proud to always have them around.",
      },
      quote: {
        pt: "O tempo é rei e faz tudo sumir, faz o infinito diminuir.",
        en: "Time is king and makes all things vanish — it makes the infinite shrink.",
      },
      interests: [
        { pt: "Anime & Mangá", en: "Anime & Manga" },
        "Steins;Gate",
        "Houseki no Kuni",
        { pt: "Comics da Merry", en: "Merry's comics" },
        "Deadlock",
      ],
    },
    {
      id: "trajetoria",
      type: "timeline",
      nav: { pt: "Trajetória", en: "Timeline" },
      kicker: { pt: "Trajetória", en: "Path" },
      title: { pt: "Linha do tempo", en: "Timeline" },
      items: [
        { year: "2021", title: "Arcanth", desc: { pt: "Desenvolvedor principal.", en: "Lead developer." } },
        {
          year: "2020",
          title: "EmpireNetwork",
          desc: { pt: "Dev do servidor de Minecraft.", en: "Minecraft server developer." },
        },
        {
          year: { pt: "Estudos", en: "Study" },
          title: "Game hacking",
          desc: { pt: "Engenharia reversa e a memória do computador.", en: "Reverse engineering and computer memory." },
        },
        {
          year: { pt: "2024-Agora", en: "2024-Now" },
          title: { pt: "Câmara de Fortaleza", en: "Fortaleza City Council" },
          desc: { pt: "Onde trabalho atualmente.", en: "Where I currently work." },
        },
        {
          year: { pt: "2023-Agora", en: "2023-Now" },
          title: { pt: "Ciências da Computação", en: "Computer Science" },
          desc: { pt: "Quase formado — depois, mestrado e doutorado.", en: "Almost graduated — then a master's and a PhD." },
        },
      ],
    },
    {
      id: "skills",
      type: "skills",
      nav: { pt: "Skills", en: "Skills" },
      kicker: "Skills",
      title: { pt: "Ferramentas & ofícios", en: "Tools & crafts" },
      skills: [
        { pt: "Engenharia reversa", en: "Reverse engineering" },
        { pt: "Análise de memória", en: "Memory analysis" },
        "C / C++",
        "Java",
        { pt: "Servidores de jogos", en: "Game servers" },
        "Game internals",
        { pt: "IoT & eletrônica", en: "IoT & electronics" },
        { pt: "Escrita criativa", en: "Creative writing" },
      ],
    },
    {
      id: "links",
      type: "links",
      nav: "Links",
      kicker: "Links",
      title: { pt: "Onde me achar", en: "Where to find me" },
      links: [
        {
          label: "Twitter / X",
          value: "@KingTimer12",
          note: { pt: "onde eu falo besteira", en: "where I ramble" },
          action: { kind: "link", href: "https://x.com/KingTimer12" },
        },
        {
          label: "GitHub",
          value: "KingTimer12",
          note: { pt: "meus códigos", en: "my codes" },
          action: { kind: "link", href: "https://github.com/kingtimer12" },
        },
        {
          label: "Discord",
          value: "wolfsbany",
          note: { pt: "Clique pra copiar", en: "Click to copy" },
          action: { kind: "copy", value: "wolfsbany" },
        },
      ],
    },
    {
      id: "contato",
      type: "contact",
      nav: { pt: "Contato", en: "Contact" },
      kicker: { pt: "Contato", en: "Contact" },
      title: { pt: "Vamos trocar uma ideia", en: "Let's talk" },
      body: {
        pt: "Tô sempre por aí pra conversar, ajudar ou só dividir um bom papo sobre código, anime e o tempo passando.",
        en: "I'm always around to chat, help out, or just share a good conversation about code, anime and time passing by.",
      },
      cta: {
        label: { pt: "Copiar meu Discord", en: "Copy my Discord" },
        doneLabel: { pt: "✓ Copiado: wolfsbany", en: "✓ Copied: wolfsbany" },
        action: { kind: "copy", value: "wolfsbany" },
      },
    },
  ],
};
